// Pro-rata pay calculator. Pure functions — no React, no DOM.
// Three UK-common methodologies, plus a custom divisor.

export const METHODS = {
  WORKING_260: 'working_260',
  CALENDAR_365: 'calendar_365',
  MONTH_WORKING: 'month_working',
  CUSTOM: 'custom',
};

export const METHOD_LABELS = {
  [METHODS.WORKING_260]: 'Working days (annual ÷ 260)',
  [METHODS.CALENDAR_365]: 'Calendar days (annual ÷ 365)',
  [METHODS.MONTH_WORKING]: 'Month-specific working days',
  [METHODS.CUSTOM]: 'Custom divisor',
};

export const METHOD_DESCRIPTIONS = {
  [METHODS.WORKING_260]:
    'The most common UK convention. Counts only Mon–Fri working days, paid at annual salary ÷ 260.',
  [METHODS.CALENDAR_365]:
    'Pay accrues every calendar day. Used where contracts say "per calendar day".',
  [METHODS.MONTH_WORKING]:
    'HMRC-style: monthly pay × (working days worked ÷ working days in that calendar month). Most accurate per HMRC but gives uneven monthly figures.',
  [METHODS.CUSTOM]:
    'Use a contractual divisor (e.g. 252, 261). Daily rate = annual ÷ divisor.',
};

export const DEFAULT_METHOD = METHODS.WORKING_260;

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  // Handle YYYY-MM-DD without timezone surprises
  const [y, m, d] = String(value).split('-').map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return new Date(value);
}

function isWorkingDay(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

function countWorkingDays(start, end) {
  if (!start || !end || end < start) return 0;
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    if (isWorkingDay(cur)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function countCalendarDays(start, end) {
  if (!start || !end || end < start) return 0;
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / 86400000) + 1;
}

function formatGBP(n) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

/**
 * Compute pro-rata pay for a partial worked window inside a payroll period.
 *
 * @param {object} args
 * @param {number|string} args.annualSalary
 * @param {string} args.method  one of METHODS.*
 * @param {Date|string} args.periodStart  payroll period start (typically first of the calendar month)
 * @param {Date|string} args.periodEnd    payroll period end (typically last of the calendar month)
 * @param {Date|string} [args.employmentStart]  start_date of new starter — clamps the worked window
 * @param {Date|string} [args.employmentEnd]    leaving_date of leaver — clamps the worked window
 * @param {number} [args.customDivisor]   only used when method = CUSTOM
 * @returns {{amount:number, daysWorked:number, totalDays:number, dailyRate:number, formula:string, method:string}|null}
 */
export function proRata({
  annualSalary,
  method = DEFAULT_METHOD,
  periodStart,
  periodEnd,
  employmentStart,
  employmentEnd,
  customDivisor,
}) {
  const annual = parseFloat(annualSalary) || 0;
  if (annual <= 0) return null;

  const ps = toDate(periodStart);
  const pe = toDate(periodEnd);
  if (!ps || !pe || pe < ps) return null;

  const es = toDate(employmentStart);
  const ee = toDate(employmentEnd);

  const windowStart = es && es > ps ? es : ps;
  const windowEnd = ee && ee < pe ? ee : pe;

  if (windowEnd < windowStart) {
    return {
      amount: 0,
      daysWorked: 0,
      totalDays: 0,
      dailyRate: 0,
      formula: 'No qualifying days in period',
      method,
    };
  }

  switch (method) {
    case METHODS.WORKING_260: {
      const daysWorked = countWorkingDays(windowStart, windowEnd);
      const dailyRate = annual / 260;
      const amount = dailyRate * daysWorked;
      return {
        amount: Math.round(amount * 100) / 100,
        daysWorked,
        totalDays: 260,
        dailyRate: Math.round(dailyRate * 100) / 100,
        formula: `${daysWorked} working day${daysWorked === 1 ? '' : 's'} × ${formatGBP(dailyRate)} (annual ÷ 260)`,
        method,
      };
    }
    case METHODS.CALENDAR_365: {
      const daysWorked = countCalendarDays(windowStart, windowEnd);
      const dailyRate = annual / 365;
      const amount = dailyRate * daysWorked;
      return {
        amount: Math.round(amount * 100) / 100,
        daysWorked,
        totalDays: 365,
        dailyRate: Math.round(dailyRate * 100) / 100,
        formula: `${daysWorked} calendar day${daysWorked === 1 ? '' : 's'} × ${formatGBP(dailyRate)} (annual ÷ 365)`,
        method,
      };
    }
    case METHODS.MONTH_WORKING: {
      const daysWorked = countWorkingDays(windowStart, windowEnd);
      const monthStart = new Date(ps.getFullYear(), ps.getMonth(), 1);
      const monthEnd = new Date(ps.getFullYear(), ps.getMonth() + 1, 0);
      const totalWorkingDays = countWorkingDays(monthStart, monthEnd);
      const monthlyPay = annual / 12;
      const amount = totalWorkingDays > 0 ? monthlyPay * (daysWorked / totalWorkingDays) : 0;
      return {
        amount: Math.round(amount * 100) / 100,
        daysWorked,
        totalDays: totalWorkingDays,
        dailyRate: totalWorkingDays > 0 ? Math.round((monthlyPay / totalWorkingDays) * 100) / 100 : 0,
        formula: `(${formatGBP(monthlyPay)} monthly) × (${daysWorked}/${totalWorkingDays} working days)`,
        method,
      };
    }
    case METHODS.CUSTOM: {
      const div = parseFloat(customDivisor) || 0;
      if (div <= 0) return null;
      const daysWorked = countWorkingDays(windowStart, windowEnd);
      const dailyRate = annual / div;
      const amount = dailyRate * daysWorked;
      return {
        amount: Math.round(amount * 100) / 100,
        daysWorked,
        totalDays: div,
        dailyRate: Math.round(dailyRate * 100) / 100,
        formula: `${daysWorked} working day${daysWorked === 1 ? '' : 's'} × ${formatGBP(dailyRate)} (annual ÷ ${div})`,
        method,
      };
    }
    default:
      return null;
  }
}

/** Daily rate for holiday pay etc. */
export function dailyRate({ annualSalary, method = DEFAULT_METHOD, periodStart, customDivisor }) {
  const annual = parseFloat(annualSalary) || 0;
  if (annual <= 0) return 0;
  switch (method) {
    case METHODS.WORKING_260:
      return Math.round((annual / 260) * 100) / 100;
    case METHODS.CALENDAR_365:
      return Math.round((annual / 365) * 100) / 100;
    case METHODS.MONTH_WORKING: {
      const ps = toDate(periodStart) || new Date();
      const monthStart = new Date(ps.getFullYear(), ps.getMonth(), 1);
      const monthEnd = new Date(ps.getFullYear(), ps.getMonth() + 1, 0);
      const total = countWorkingDays(monthStart, monthEnd);
      return total > 0 ? Math.round(((annual / 12) / total) * 100) / 100 : 0;
    }
    case METHODS.CUSTOM: {
      const div = parseFloat(customDivisor) || 0;
      return div > 0 ? Math.round((annual / div) * 100) / 100 : 0;
    }
    default:
      return 0;
  }
}

/** Helpers for the UI to default the period to the calendar month containing a date. */
export function monthBounds(date) {
  const d = toDate(date) || new Date();
  return {
    start: new Date(d.getFullYear(), d.getMonth(), 1),
    end: new Date(d.getFullYear(), d.getMonth() + 1, 0),
  };
}

export function toIsoDate(d) {
  if (!d) return '';
  const date = toDate(d);
  if (!date || isNaN(date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
